<template>
  <md-card class="stock-value-widget">
    <md-card-header class="md-card-header-icon md-card-header-green">
      <div class="card-icon">
        <md-icon>inventory_2</md-icon>
      </div>
      <h4 class="title">{{ $t("stockValue.title") }}</h4>
    </md-card-header>
    <md-card-content>
      <div v-if="isLoading" class="stock-value-loading">{{ $t("stockValue.loading") }}</div>
      <div v-else-if="!hasAnyData" class="stock-value-empty">{{ $t("stockValue.empty") }}</div>
      <template v-else>
        <!-- Grafik / Tabelle (Trello-Update 28.08.2026): Tabelle mit Veraenderung zum Vormonat + Kopieren -->
        <div class="stock-value-toolbar">
          <select v-if="mode === 'table'" v-model="tableMonth" class="stock-value-month">
            <option v-for="p in monthsWithData" :key="p.month" :value="p.month">{{ monthTitle(p.month) }}</option>
          </select>
          <span v-else></span>
          <span class="stock-value-toolbar__right">
            <md-button v-if="mode === 'table'" class="md-simple md-sm stock-value-copy" @click="copyReport">
              <md-icon>{{ copied ? "check" : "content_copy" }}</md-icon>
              &nbsp;{{ copied ? $t("stockValue.copied") : $t("stockValue.copy") }}
            </md-button>
            <span class="stock-value-modes">
              <button class="stock-value-modes__btn" :class="{ 'is-active': mode === 'chart' }" :title="$t('stockValue.modeChart')" @click="setMode('chart')"><md-icon>show_chart</md-icon></button>
              <button class="stock-value-modes__btn" :class="{ 'is-active': mode === 'table' }" :title="$t('stockValue.modeTable')" @click="setMode('table')"><md-icon>table_rows</md-icon></button>
            </span>
          </span>
        </div>
        <div v-if="mode === 'table'" class="stock-value-table-wrap">
          <table class="stock-value-table">
            <thead>
              <tr>
                <th></th>
                <th class="num">{{ tablePoint ? monthTitle(tablePoint.month) : "" }}</th>
                <th class="num">{{ $t("stockValue.deltaHead") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tableRows" :key="row.key" :class="{ 'is-total': row.key === 'total', 'is-ship': row.key === 'ship' }">
                <td><span class="legend-dot" :style="{ background: row.color }"></span>{{ row.label }}</td>
                <td class="num">{{ formatEur(row.value) }}</td>
                <td class="num" :class="deltaClass(row.delta)">{{ formatDelta(row.delta) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-show="mode === 'chart'" class="stock-value-chart-wrap">
          <div ref="chart" class="ct-chart stock-value-chart"></div>
          <div v-if="tooltip.visible" ref="tooltipEl" class="stock-value-tooltip" :style="{ left: tooltip.x + 'px', top: tooltip.y + 'px' }">
            <div class="tt-month">{{ tooltip.label }}</div>
            <!-- 7 Linien (Trello 26.08.2026): Summe, je Lagerort, Auf Schiff -->
            <div v-for="row in tooltip.rows" :key="row.key" class="tt-row">
              <span class="legend-dot" :style="{ background: row.color }"></span>{{ row.label }}: {{ formatEur(row.value) }} <span class="tt-delta">{{ formatDelta(row.delta) }}</span>
            </div>
          </div>
        </div>
        <div v-show="mode === 'chart'" class="stock-value-legend">
          <span v-for="row in seriesDefs" :key="row.key" class="legend-item"><span class="legend-dot" :style="{ background: row.color }"></span>{{ row.label }}</span>
        </div>
      </template>
    </md-card-content>
  </md-card>
</template>
<script>
import axiosFactory from "../../services/axios.factory";

// Farben je Linie: Summe gruen, Lagerorte in fester Palette, Schiff orange (Legende + Tooltip + SVG)
const MODE_KEY = "occ.stockValue.mode";
const SERIES_COLORS = {
  total: "#7f915a",
  warehouses: ["#2196f3", "#9c27b0", "#00bcd4", "#795548", "#607d8b", "#e91e63"],
  ship: "#ff9800",
};

export default {
  name: "StockValueWidget",
  data() {
    return {
      isLoading: true,
      points: [],
      chart: null,
      tooltip: { visible: false, x: 0, y: 0, label: "", rows: [] },
      mode: this.readMode(),
      tableMonth: null,
      copied: false,
    };
  },
  computed: {
    hasAnyData() {
      return this.points.some((p) => p.total != null);
    },
    // Lagerorte in Serverreihenfolge (Warehouses.Id: PP, WI, VV, BS, TT) - Union ueber alle Monate,
    // damit ein Lager, das in einem alten Monat fehlt, trotzdem eine Linie bekommt.
    warehouseCodes() {
      const codes = [];
      this.points.forEach((p) => (p.warehouses || []).forEach((w) => !codes.includes(w.code) && codes.push(w.code)));
      return codes;
    },
    monthsWithData() {
      return this.points.filter((p) => p.total != null);
    },
    tablePoint() {
      return this.monthsWithData.find((p) => p.month === this.tableMonth) || this.monthsWithData[this.monthsWithData.length - 1] || null;
    },
    // Zeilen der Tabelle: Wert des gewaehlten Monats + Veraenderung zum Vormonat (null = kein Vormonat)
    tableRows() {
      const p = this.tablePoint;
      if (!p) return [];
      return this.seriesDefs.map((d) => ({ key: d.key, label: d.label, color: d.color, value: d.get(p), delta: this.deltaFor(d, p) }));
    },
    // Reihenfolge = Kundenwunsch: Summe, PP, WI, VV, BS, TT, Auf Schiff
    seriesDefs() {
      const defs = [{ key: "total", label: this.$t("stockValue.total"), color: SERIES_COLORS.total, className: "sv-total", get: (p) => p.total }];
      this.warehouseCodes.forEach((code, i) => {
        defs.push({
          key: "wh-" + code,
          label: code,
          color: SERIES_COLORS.warehouses[i % SERIES_COLORS.warehouses.length],
          className: "sv-wh-" + (i % SERIES_COLORS.warehouses.length),
          get: (p) => {
            const w = (p.warehouses || []).find((x) => x.code === code);
            return w ? w.value : null;
          },
        });
      });
      defs.push({ key: "ship", label: this.$t("stockValue.onShip"), color: SERIES_COLORS.ship, className: "sv-ship", get: (p) => p.stockOnShip });
      return defs;
    },
  },
  methods: {
    readMode() {
      try {
        return localStorage.getItem(MODE_KEY) === "table" ? "table" : "chart";
      } catch (e) {
        return "chart";
      }
    },
    setMode(m) {
      this.mode = m;
      this.tooltip.visible = false;
      try {
        localStorage.setItem(MODE_KEY, m);
      } catch (e) {
        /* nur Komfort */
      }
      if (m === "chart") this.$nextTick(() => this.chart && this.chart.update());
    },
    monthTitle(iso) {
      const d = new Date(iso);
      return d.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
    },
    // Vormonat = der Punkt davor auf der Achse; ohne Daten dort bleibt die Differenz leer
    deltaFor(def, point) {
      const idx = this.points.indexOf(point);
      const prev = idx > 0 ? this.points[idx - 1] : null;
      const cur = def.get(point);
      const before = prev ? def.get(prev) : null;
      if (cur == null || before == null) return null;
      // Differenz der gerundeten Euro-Betraege, damit sie zu den angezeigten Zahlen passt (Karte: 8.436.184 - 7.239.869)
      return Math.round(Number(cur)) - Math.round(Number(before));
    },
    formatDelta(delta) {
      if (delta == null) return "(–)";
      const sign = delta >= 0 ? "+" : "-";
      return "(" + sign + Math.abs(Math.round(delta)).toLocaleString("de-DE") + " €)";
    },
    deltaClass(delta) {
      if (delta == null) return "";
      return delta >= 0 ? "delta-up" : "delta-down";
    },
    // Klartext exakt wie in der Trello-Karte (gleiches Format schickt der Monatsjob per Mail)
    buildReport() {
      const p = this.tablePoint;
      if (!p) return "";
      const title = this.monthTitle(p.month);
      const euro = (v) => Math.round(Number(v)).toLocaleString("de-DE") + " Euro";
      const line = (row) => {
        const delta = row.delta == null ? "(–)" : "(" + (row.delta >= 0 ? "+" : "-") + euro(Math.abs(row.delta)) + ")";
        return (row.label + ":").padEnd(12) + euro(row.value).padEnd(16) + delta;
      };
      const rows = this.tableRows;
      const total = rows.find((r) => r.key === "total");
      const ship = rows.find((r) => r.key === "ship");
      const wh = rows.filter((r) => r.key.startsWith("wh-"));
      const out = ["Vorräte " + title, "", " ".repeat(12) + title.padEnd(16) + "Veränderung ggü. Vormonat", "-".repeat(53), line(total), ""];
      wh.forEach((r) => out.push(line(r)));
      out.push(line(ship));
      return out.join("\n");
    },
    async copyReport() {
      const text = this.buildReport();
      if (!text) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement("textarea");
          ta.value = text;
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        this.copied = true;
        setTimeout(() => (this.copied = false), 2000);
      } catch (e) {
        this.copied = false;
      }
    },
    monthLabel(iso) {
      const d = new Date(iso);
      const months = this.$t("stockValue.monthsShort");
      const m = Array.isArray(months) ? months[d.getMonth()] : d.getMonth() + 1;
      return `${m} ${String(d.getFullYear()).slice(2)}`;
    },
    formatEurShort(value) {
      if (value == null) return "";
      if (Math.abs(value) >= 1000000) return (value / 1000000).toLocaleString("de-DE", { maximumFractionDigits: 2 }) + " Mio";
      if (Math.abs(value) >= 1000) return Math.round(value / 1000).toLocaleString("de-DE") + "k";
      return Math.round(value).toLocaleString("de-DE");
    },
    formatEur(value) {
      if (value == null) return "–";
      return value.toLocaleString("de-DE", { maximumFractionDigits: 0 }) + " €";
    },
    renderChart() {
      if (!this.$refs.chart) return;
      const labels = this.points.map((p) => this.monthLabel(p.month));
      // Chartist: className je Serie statt ct-series-a/b/c, damit die Farben unabhaengig von der
      // Anzahl der Lagerorte stimmen
      const series = this.seriesDefs.map((d) => ({
        className: d.className,
        data: this.points.map((p) => {
          const v = d.get(p);
          return v != null ? Number(v) : null;
        }),
      }));
      const self = this;
      this.chart = new this.$Chartist.Line(
        this.$refs.chart,
        { labels, series },
        {
          fullWidth: true,
          showArea: false,
          lineSmooth: this.$Chartist.Interpolation.none(),
          // top: Platz fuer das oberste Y-Label (zweizeilig "8 / Mio"), sonst wird die Zahl abgeschnitten
          chartPadding: { top: 28, right: 24, bottom: 8, left: 8 },
          axisY: {
            onlyInteger: false,
            // Genug Abstand zwischen den Beschriftungen, sonst crowden sie bei hoher Kachel.
            scaleMinSpace: 40,
            labelInterpolationFnc: (v) => self.formatEurShort(v),
          },
          axisX: {
            showGrid: false,
            // Bei 1/3-Breite jede zweite Monatsbeschriftung (vom aktuellen Monat aus), sonst zu gedrängt.
            labelInterpolationFnc: (value, index, labels) => ((labels.length - 1 - index) % 2 === 0 ? value : null),
          },
        }
      );

      // Beim Resizen der Kachel neu zeichnen (Chartist reflowt sonst nur bei window-resize).
      if (window.ResizeObserver && this.$refs.chart) {
        this.resizeObserver = new ResizeObserver(() => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => {
            if (this.chart) this.chart.update();
          }, 80);
        });
        this.resizeObserver.observe(this.$refs.chart);
      }

      // Mouseover-Tooltip auf den Datenpunkten: Monat + alle Werte (Summe, Lagerorte, Schiff).
      this.chart.on("draw", (data) => {
        if (data.type !== "point") return;
        const node = data.element._node;
        const idx = data.index;
        node.addEventListener("mouseenter", () => this.showTooltip(idx, node));
        node.addEventListener("mouseleave", () => {
          this.tooltip.visible = false;
        });
      });
    },
    showTooltip(index, node) {
      const p = this.points[index];
      if (!p || p.total == null) return;
      const wrap = this.$refs.chart && this.$refs.chart.parentElement;
      if (!wrap) return;
      const r = node.getBoundingClientRect();
      // position: fixed -> Viewport-Koordinaten; so schneidet das overflow:hidden der Kachel nichts ab
      this.tooltip = {
        visible: true,
        x: r.left + r.width / 2,
        y: r.top,
        label: this.monthLabel(p.month),
        rows: this.seriesDefs.map((d) => ({ key: d.key, label: d.label, color: d.color, value: d.get(p), delta: this.deltaFor(d, p) })),
      };
      // Nach dem Rendern im Viewport halten (rechter/linker Rand, oberer Rand)
      this.$nextTick(() => {
        const el = this.$refs.tooltipEl;
        if (!el) return;
        const half = el.offsetWidth / 2;
        const min = half + 4;
        const max = window.innerWidth - half - 4;
        this.tooltip.x = Math.min(Math.max(this.tooltip.x, min), Math.max(min, max));
        if (this.tooltip.y - el.offsetHeight - 8 < 0) this.tooltip.y = el.offsetHeight + 8 + r.height;
      });
    },
    async load() {
      try {
        const res = await axiosFactory().get("/reporting/monthly_stock_value");
        this.points = res.data.data || [];
      } catch (e) {
        this.points = [];
      } finally {
        this.isLoading = false;
        this.$nextTick(() => {
          if (this.hasAnyData) {
            const last = this.monthsWithData[this.monthsWithData.length - 1];
            this.tableMonth = last ? last.month : null;
            this.renderChart();
          }
        });
      }
    },
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    clearTimeout(this.resizeTimer);
    if (this.chart) this.chart.detach();
  },
};
</script>
<style lang="scss">
.stock-value-widget {
  .md-card-content {
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
    // Chart passt sich der Kachel an — nichts zu scrollen; ohne dies zeigt die Karte
    // einen Querscrollbalken, sobald das SVG minimal breiter rendert als die Kachel
    overflow: hidden;
  }
  .stock-value-chart-wrap {
    position: relative;
    flex: 1;
    min-height: 160px;
    overflow: hidden;
  }

  .stock-value-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
    min-height: 30px;

    &__right {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
  }
  .stock-value-modes {
    display: inline-flex;
    border: 1px solid #ddd;
    border-radius: 6px;
    overflow: hidden;
    background: #fff;

    &__btn {
      border: none;
      background: #fff;
      width: 34px;
      height: 28px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;

      & + & {
        border-left: 1px solid #ddd;
      }
      .md-icon {
        font-size: 18px !important;
        color: #888 !important;
      }
      &.is-active {
        background: #7f915a;

        .md-icon {
          color: #fff !important;
        }
      }
    }
  }
  .stock-value-table-wrap {
    flex: 1;
    overflow: auto;
  }
  .stock-value-month {
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 13px;
    background: #fff;
  }
  .stock-value-copy {
    margin: 0 !important;
  }
  .stock-value-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    font-variant-numeric: tabular-nums;

    th {
      text-align: left;
      color: #666;
      font-weight: 500;
      font-size: 12px;
      padding: 4px 8px;
      border-bottom: 1px solid #eee;
    }
    td {
      padding: 4px 8px;
      border-bottom: 1px solid #f3f3f3;
      white-space: nowrap;
    }
    .num {
      text-align: right;
    }
    .legend-dot {
      margin-right: 6px;
    }
    .is-total td {
      font-weight: 600;
      border-bottom: 2px solid #e0e0e0;
    }
    .is-ship td {
      border-top: 1px solid #e0e0e0;
    }
    .delta-up {
      color: #7f915a;
    }
    .delta-down {
      color: #c62828;
    }
  }
  .tt-delta {
    opacity: 0.8;
    margin-left: 4px;
  }

  .stock-value-tooltip {
    position: fixed;
    transform: translate(-50%, -100%);
    margin-top: -8px;
    background: rgba(33, 33, 33, 0.92);
    color: #fff;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    line-height: 1.5;
    white-space: nowrap;
    pointer-events: none;
    z-index: 1100;

    .tt-month {
      font-weight: 600;
      margin-bottom: 2px;
    }
    .tt-row {
      display: flex;
      align-items: center;
      gap: 5px;
    }
  }

  .stock-value-chart {
    height: 100%;

    // Farben muessen zu SERIES_COLORS im Script passen
    .sv-total .ct-line,
    .sv-total .ct-point {
      stroke: #7f915a;
    }
    .sv-wh-0 .ct-line,
    .sv-wh-0 .ct-point {
      stroke: #2196f3;
    }
    .sv-wh-1 .ct-line,
    .sv-wh-1 .ct-point {
      stroke: #9c27b0;
    }
    .sv-wh-2 .ct-line,
    .sv-wh-2 .ct-point {
      stroke: #00bcd4;
    }
    .sv-wh-3 .ct-line,
    .sv-wh-3 .ct-point {
      stroke: #795548;
    }
    .sv-wh-4 .ct-line,
    .sv-wh-4 .ct-point {
      stroke: #607d8b;
    }
    .sv-wh-5 .ct-line,
    .sv-wh-5 .ct-point {
      stroke: #e91e63;
    }
    .sv-ship .ct-line,
    .sv-ship .ct-point {
      stroke: #ff9800;
    }
    .ct-line {
      stroke-width: 2px;
    }
    .ct-point {
      stroke-width: 6px;
    }
  }

  .stock-value-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    justify-content: center;
    margin-top: 8px;
    font-size: 12px;

    .legend-item {
      display: inline-flex;
      align-items: center;
      gap: 5px;
    }
    .legend-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      display: inline-block;
    }
  }

  .stock-value-loading,
  .stock-value-empty {
    padding: 40px 8px;
    text-align: center;
    color: #999;
    font-size: 13px;
  }
}
</style>
